import { AuthProvider } from "@/context/with-auth";
import { NotificationsProvider } from '@toolpad/core/useNotifications';
import React from "react";
import { createTheme, ThemeProvider, Theme } from '@mui/material/styles';
import { useSearchParams } from "next/navigation";
import { DM_Sans } from "next/font/google";
import localFont from "next/font/local";

import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
import {usePathname, useRouter} from "next/dist/client/components/navigation";
import Cookies from "js-cookie";
import {Rfs__all} from "../../_refs/Rfs__all";
import GlobalErrorObserver from "./GlobalErrorObserver";

const dmSans = DM_Sans({
    subsets: ["latin"]
});

const iranSans = localFont({
    src: [
        { path: "../../public/fonts/Dana-Regular.woff", weight: "400", style: "regular" },
        { path: "../../public/fonts/Dana-Medium.woff", weight: "500", style: "medium" },
        { path: "../../public/fonts/Dana-Bold.woff", weight: "700", style: "bold" },
    ],
    variable: "--font-iransans",
});

// cache برای RTL
const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

// cache برای LTR (بدون rtlPlugin)
const cacheLtr = createCache({
    key: "muiltr",
});

const getTheme = (outerTheme: Theme, dir: "rtl" | "ltr") =>
    createTheme({
        direction: dir,
        palette: {
            primary: {
                main: "#704DFF",
                contrastText: "#fff",
            },
            success: {
                main: "#07A287",
            },
            warning: {
                main: "#FFAA15",
            },
        },
        typography: {
            fontFamily: dir === "rtl" ? iranSans.style.fontFamily : dmSans.style.fontFamily,
        },
    });




export function Providers({ children }: { children: React.ReactNode }) {


    Rfs__all.glob_provider()


    const searchParams = useSearchParams();
    const pathname = usePathname();

    const [locale, setLocale] = React.useState<"en" | "fa" | "fr">(() => {
        if (typeof window !== 'undefined') {
            return (Cookies.get('language') || 'en') as "en" | "fa" | "fr";
        }
        return "en";
    });

    React.useEffect(() => {
        const isConsultation = pathname?.indexOf('/consultation') !== -1;
        const supportedLangs = isConsultation ? ['en', 'fa', 'fr'] : ['en', 'fr'];
        
        const sp = searchParams as any;
        const langParam = sp?.get('lang');
        
        if (langParam && supportedLangs.includes(langParam)) {
            const currentLang = Cookies.get('language') || 'en';
            if (currentLang !== langParam) {
                Cookies.set('language', langParam);
                setLocale(langParam as "en" | "fa" | "fr");
            }
        } else {
            const currentLang = (Cookies.get('language') || 'en') as "en" | "fa" | "fr";
            
            if (!supportedLangs.includes(currentLang)) {
                Cookies.set('language', 'en');
                setLocale('en');
            } else {
                setLocale(currentLang);
            }
        }
    }, [searchParams, pathname]);

    const rtlLocales = ["fa"];
    const dir = rtlLocales.includes(locale) ? "rtl" : "ltr";
    const cache = dir === "rtl" ? cacheRtl : cacheLtr;
    const textAlignClass = dir === "rtl" ? "text-right" : "text-left";

    React.useEffect(() => {
        document.documentElement.dir = dir;
    }, [dir]);

    return (
        <CacheProvider value={cache}>
            <ThemeProvider theme={getTheme({ palette: { mode: "light" } } as Theme, dir)}>
                <div dir={dir} className={textAlignClass}>
                    <NotificationsProvider
                        slotProps={{
                            snackbar: {
                                anchorOrigin: { vertical: "top", horizontal: "center" },
                            },
                        }}
                    >
                        <GlobalErrorObserver />
                        <AuthProvider>{children}</AuthProvider>
                    </NotificationsProvider>
                </div>
            </ThemeProvider>
        </CacheProvider>
    );
}
